import React, { useContext, useEffect, useState } from 'react'
import { Box, Button, Chip, Stack, Typography, useMediaQuery } from '@mui/material'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import { Add } from '@mui/icons-material'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import ArchivedReportPeriodService from '../../../../shared/services/archived-report-period/archived-report-period.service'
import { IPostArchivedReportPeriod } from '../../../../shared/services/archived-report-period/archived-report-period.model'
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service'
import TargetSettingService from '../../../../shared/services/target-setting/target-setting.service'
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message'
import { AddArchivedReportPeriodDialog } from '../../../dialogs/settings/report-period/add-archived-report-period-dialog'
import { CloseReportPeriodDialog } from '../../../dialogs/settings/report-period/close-report-period-dialog'
import { SettingsMobileNavbar } from '../../../../root-components/navigation/settings-mobile-navbar'
import { LoadingPage } from '../../loading-page'
import { IArchivedAndReportPeriodTableData } from '../../../../shared/services/report-period/report-period.model'
import { useHasOpenReportPeriod } from '../../../../hooks/use-has-open-report-period'
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'

export const SettingsReportingPeriodPage = () => {
  const navigate = useNavigate();
  //Services
  const archivedReportPeriodService = new ArchivedReportPeriodService();
  const reportPeriodService = new ReportPeriodService();
  const targetSettingService = new TargetSettingService()
  //Context
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext);
  //Hooks 
  const hasOpenReport = useHasOpenReportPeriod([
    reportPeriodContext.allReportPeriods,
    reportPeriodContext.lastSelectedOpenReportId,
  ]);
  //State
  const [allReportPeriods, setAllReportPeriods] = useState<IArchivedAndReportPeriodTableData[]>()
  const [openDialogNew, setDialogOpenNew] = React.useState(false);
  const [closeReportDialogOpen, setCloseReportDialogOpen] = useState<boolean>(false);
  const [openDialogOld, setDialogOpenOld] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasReductionPlan, setHasReductionPlan] = useState<boolean>(false)
  //Table State
  const [selected, setSelected] = useState<string[]>([]);
  const [inputPage, setInputPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [change, setChange] = useState<number>(0)
  const [orderBy, setOrderBy] = useState<string>()
  const [order, setOrder] = useState<'asc' | 'desc'>()
  //LoadingState
  const [hasPageMounted, setHasPageMounted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isDialogSubmitting, setIsDialogSubmitting] = useState<boolean>(false)
  //Layout
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  //Use Effects
  useEffect(() => {
    targetSettingService.getTargetSettings()
      .then(response => {
        if (response.length > 0) {
          setHasReductionPlan(true)
        }
      })
      .catch(err => console.log(err))
  }, [])

  useEffect(() => {
    getAllReportPeriods();
  }, [inputPage, perPage, change])

  useEffect(() => {
    if (hasPageMounted) {
      if (inputPage === 0) {
        setChange(change + 1)
      } else {
        setInputPage(0)
      }
    }
    setHasPageMounted(true)
  }, [order, orderBy])

  const getAllReportPeriods = () => {
    reportPeriodService.getReportPeriodAndArchivedReportPeriodsWithPagination(inputPage, perPage, orderBy, order)
      .then(response => {
        setAllReportPeriods(response.rows)
        setTotalRecords(response.count);
        setIsLoading(false)
      })
      .catch(err => {
        setIsLoading(false)
        console.log(err)
      })
  }

  //Table Contents
  const headCells: IHeadCell[] = [
    { id: 'friendlyName', numeric: false, label: 'Name', sortable: true, isHeading: true },
    { id: 'startDate', numeric: false, label: 'Start Date', sortable: true },
    { id: 'endDate', numeric: false, label: 'End Date', sortable: true },
    { id: 'locked', numeric: false, label: 'Status', sortable: true, isSubHeading: true },
    { id: 'manualEntry', numeric: false, label: 'Data Entry', sortable: true, includeInMobile: false },
  ];

  const rows = !allReportPeriods ? [] : allReportPeriods.map((data: any): any => {
    return {
      id: data.id,
      friendlyName: (
        <Stack display='flex' flexDirection='row' alignItems='center' id={data.friendlyName}>
          {data.friendlyName}
          {data.baseYear && <Chip variant='outlined' label='Base Year' color='primary' sx={{ ml: 2 }} />}
        </Stack>
      ),
      startDate: moment(data.startDate).format('DD/MM/YYYY'),
      endDate: moment(data.endDate).format('DD/MM/YYYY'),
      locked: data.locked ? 'Closed' : 'Open',
      manualEntry: data.manualEntry ? 'Manual' : 'Calculated',
    }
  })

  // Table Event Handlers
  const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
    setOrderBy(orderBy)
    setOrder(order)
  };

  const handleSelected = (selectedRowId: string[]) => {
    if (selectedRowId[0] !== undefined) {
      navigate(`/settings/company-settings/reporting-periods/${selectedRowId[0]}`)
    }
  }

  // Start New Report Period Dialog
  const closeDialogHandlerNew = (confirmed: boolean) => {
    setDialogOpenNew(false);
  };

  // Start New Archived Report Period Dialog
  const submitDialogHandler = (values: IPostArchivedReportPeriod, resetForm: () => void) => {
    if (!isDialogSubmitting) {
      setIsDialogSubmitting(true)
      archivedReportPeriodService.createArchivedReportPeriod(values)
        .then(response => {
          setActiveStep(0)
          closeDialogHandlerOld()
          reportPeriodContext.forceUpdateReportPeriods();
          userNotificationsContext.openSnackbar({ open: true, message: `Saved successfully` });
          getAllReportPeriods();
          resetForm();
          setIsDialogSubmitting(false)
        }
        )
        .catch(err => {
          setErrorMessage(err.response.data.message)
          setIsDialogSubmitting(false)
        })
    }
  }

  const closeDialogHandlerOld = () => {
    setDialogOpenOld(false);
  };

  // Component Event Handlers 
  const openDialogHandlerNew = () => {
    setCloseReportDialogOpen(false);
    setDialogOpenNew(true);
  };

  const openDialogHandlerOld = () => {
    setCloseReportDialogOpen(false);
    setDialogOpenOld(true);
    setErrorMessage('')
  }

  return (
    <Box sx={{ pb: isMobileModeMd ? 3 : 6, pl: isMobileModeMd ? 3 : 6, pr: isMobileModeMd ? 3 : 6, pt: isMobileModeMd ? 3 : 5, mt: isMobileModeMd ? 5 : 0, }}>
      {isMobileModeSm && <Box sx={{ pb: 4 }}>
        <SettingsMobileNavbar />
      </Box>}
      <Stack direction={isMobileModeMd ? 'column' : 'row'} justifyContent='space-between' alignItems={isMobileModeMd ? 'flex-start' : 'center'} sx={{ mb: 4 }}>
        <Typography variant='h4' sx={{ color: 'text.primary', pb: 1 }}>Reporting Periods</Typography>
        <Stack display='flex' direction={isMobileModeMd ? 'column' : 'row'} spacing={1}>
          <Button variant='text' onClick={() => openDialogHandlerOld()}>
            <Add sx={{ color: 'primary.main' }} />
            <Typography>Add Archived Report Period</Typography>
          </Button>
          <Button onClick={() => openDialogHandlerNew()}>
            <Add sx={{ color: 'white' }} />
            <Typography>Add Report Period</Typography>
          </Button>
        </Stack>
      </Stack>
      {hasOpenReport !== undefined && hasOpenReport === false &&
        <ClosedReportPeriodMessage
          openDialog={openDialogHandlerNew} />
      }
      <LoadingPage pageReady={!isLoading}>
        {allReportPeriods &&
          <TableDisplay
            handleSelected={handleSelected}
            isMobileMode={isMobileModeMd}
            headCells={headCells}
            rows={rows}
            selected={selected}
            setSelected={setSelected}
            inputPage={inputPage}
            setInputPage={setInputPage}
            totalRecords={totalRecords}
            perPage={perPage}
            setPerPage={setPerPage}
            onOrderChange={handleOrderChange} />
        }
      </LoadingPage>
      {openDialogNew &&
        <StartNewReportPeriodDialog
          open={openDialogNew}
          closeHandler={closeDialogHandlerNew}
          getAllReportPeriods={getAllReportPeriods}
        />}
      {openDialogOld &&
        <AddArchivedReportPeriodDialog
          open={openDialogOld}
          isSubmitting={isDialogSubmitting}
          closeHandler={closeDialogHandlerOld}
          submitHandler={submitDialogHandler}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
        />}
    </Box>
  )
}